import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MessageSquare, Users, School, ChefHat, Building2, Zap, Clock, CheckCircle, Bell, FileText, Archive, Search, Shield } from 'lucide-react';
import Header from '../components/Header';
import '../styles/index.css';

const PageEcoLoop = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - EcoLoop | Messagerie"; }, []);

  return (
    <div className="home-container">
      <Header currentPage="ecoloop" lang="fr" />

      {/* Hero EcoLoop */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <MessageSquare size={16} />
            <span>Messagerie Intégrée</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span className="gradient-text">EcoLoop</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.2rem', lineHeight: '1.8' }}>
            La messagerie structurée qui centralise toutes vos communications alimentaires
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">4</div>
              <div className="stat-label">Types de canaux</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Traçabilité</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">5h</div>
              <div className="stat-label">Économisées / semaine</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section problem-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Le chaos de la communication actuelle</h2>
            <p className="section-subtitle">
              Emails, appels, SMS... L'information se perd et personne n'a de vue d'ensemble
            </p>
          </div>

          <div className="problems-grid">
            <div className="problem-card">
              <div className="problem-icon"><MessageSquare size={32} style={{color: '#84CC16'}} /></div>
              <h3>Emails dispersés</h3>
              <p>Conversations perdues dans des boîtes email multiples. Impossible de retrouver l'historique.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><Bell size={32} style={{color: '#84CC16'}} /></div>
              <h3>Appels non documentés</h3>
              <p>Décisions prises par téléphone sans trace écrite. Conflits et malentendus fréquents.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><Search size={32} style={{color: '#84CC16'}} /></div>
              <h3>Zéro traçabilité</h3>
              <p>Qui a dit quoi, quand, et pourquoi ? Impossible de savoir sans fouiller partout.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><Clock size={32} style={{color: '#84CC16'}} /></div>
              <h3>Temps perdu</h3>
              <p>Heures passées à chercher des informations, reposer les mêmes questions, clarifier les malentendus.</p>
            </div>
          </div>

          <div className="solution-intro">
            <h3>EcoLoop résout tout ça</h3>
            <p>Une messagerie intégrée qui centralise, structure, et archive toutes vos communications</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Fonctionnalités EcoLoop</h2>
            <p className="section-subtitle">
              Tout ce dont vous avez besoin pour communiquer efficacement
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Messagerie temps réel</h3>
              <p>Conversations instantanées entre tous les acteurs. Notifications push pour réponses rapides.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Canaux dédiés par acteur</h3>
              <p>Parents, Traiteurs, Écoles, Directions. Chaque relation a son canal structuré.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Système de tickets</h3>
              <p>Gérez les incidents avec des tickets structurés. Statut, priorité, résolution.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Archive size={32} /></div>
              <h3>Archivage automatique</h3>
              <p>Toutes les conversations archivées automatiquement. Historique complet accessible en un clic.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Search size={32} /></div>
              <h3>Recherche puissante</h3>
              <p>Retrouvez n'importe quelle conversation ou décision en secondes avec la recherche full-text.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Notifications intelligentes</h3>
              <p>Alertes personnalisées selon urgence et rôle. Ne manquez jamais un message important.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Sécurisé & Privé</h3>
              <p>Chiffrement de bout en bout. Chaque acteur voit uniquement ses conversations pertinentes.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Accusés de réception</h3>
              <p>Sachez quand vos messages sont lus. Suivi de statut (envoyé, lu, répondu).</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Pièces jointes</h3>
              <p>Partagez documents, photos, menus. Tout reste dans le contexte de la conversation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="section coparent-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Cas d'usage EcoLoop</h2>
            <p className="section-subtitle">
              Comment chaque acteur utilise la messagerie au quotidien
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Parents → Traiteurs</h3>
              <p><strong>Exemple :</strong> "Mon enfant est allergique aux noix. Pouvez-vous confirmer que le menu de jeudi est sûr ?"<br/>
              Réponse documentée et traçable.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><School size={32} /></div>
              <h3>Écoles → Traiteurs</h3>
              <p><strong>Exemple :</strong> "Nous avons un événement spécial vendredi. Pouvons-nous ajuster le volume à 150 repas ?"<br/>
              Coordination simplifiée.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><ChefHat size={32} /></div>
              <h3>Traiteurs → Écoles</h3>
              <p><strong>Exemple :</strong> "Nouveau menu disponible la semaine prochaine. Voici les options avec valeurs nutritionnelles."<br/>
              Partage d'information structuré.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Building2 size={32} /></div>
              <h3>Directions → Écoles</h3>
              <p><strong>Exemple :</strong> "Nouvelle politique nutritionnelle à implémenter. Voici les directives pour tous les établissements."<br/>
              Communication descendante efficace.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Gestion d'incidents</h3>
              <p><strong>Exemple :</strong> Repas manquant signalé → Ticket créé → Traiteur notifié → Résolution documentée → Ticket fermé.<br/>
              Processus clair et traçable.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Feedback & Amélioration</h3>
              <p><strong>Exemple :</strong> École partage les notes de satisfaction des élèves avec le traiteur pour ajuster les menus.<br/>
              Boucle d'amélioration continue.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="section roi-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Pourquoi EcoLoop change tout</h2>
            <p className="section-subtitle">
              Les bénéfices concrets pour votre organisation
            </p>
          </div>

          <div className="roi-grid">
            <div className="roi-card">
              <div className="roi-metric">
                <Clock size={40} />
                <h3>Gain de temps massif</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Fini les recherches d'emails interminables. Toute l'information 
                centralisée et accessible instantanément. <strong>Économisez 5h/semaine minimum</strong> 
                par personne impliquée.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <Shield size={40} />
                <h3>Traçabilité complète</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Chaque décision documentée. Chaque conversation archivée. <strong>Zéro risque de litige</strong> 
                grâce à l'historique complet et immuable.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <Zap size={40} />
                <h3>Réactivité maximale</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Notifications instantanées. <strong>Résolvez les problèmes 3x plus vite</strong> 
                qu'avec email/téléphone grâce à la communication structurée.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/caterers')}>Pour les Traiteurs</a>
                <a onClick={() => navigate('/parents')}>Pour les Parents</a>
                <a onClick={() => navigate('/schools')}>Pour les Écoles</a>
                <a onClick={() => navigate('/directions')}>Pour les Directions</a>
                <a onClick={() => navigate('/css')}>Pour les CSS</a>
              </div>
              <div className="footer-column">
                <h4>Technologies</h4>
                <a onClick={() => navigate('/coparentalite')}>Co-Parentalité</a>
                <a onClick={() => navigate('/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/ecoloop')}>EcoLoop</a>
                <a onClick={() => navigate('/platform')}>Plateforme</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. Tous droits réservés.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageEcoLoop;
